import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RefreshCw } from "lucide-react";
import { API, useFeed, useMarkets } from "./ui";
import { agoSec, base, money, pct, tone } from "./format";

export const MARKET_VIEWS = [
  { id: "live", label: "Live Desk" },
  { id: "performance", label: "Performance" },
  { id: "risk", label: "Risk & Accounts" },
  { id: "ledger", label: "Trade Ledger" },
];

const MACRO = (m) => {
  const s = String(m || "").toUpperCase();
  return s.includes("BULL") ? "BULL" : s.includes("BEAR") ? "BEAR" : "NEUTRAL";
};
const lower = (s) => String(s || "").replace(/_/g, " ").toLowerCase();

function RowA({ session, pulse, regime, onRefresh, refreshing }) {
  const tilt = regime?.bot_tilt || {};
  const macro = MACRO(regime?.macro);
  // `regime_label` is a hard-coded default until something computes it
  // (regime_label_source === "default"); show NEXUS's live regime instead.
  const regimeDetail = regime
    ? (regime.regime_label_source === "computed"
      ? lower(regime.regime_label)
      : [lower(regime.regime?.regime), lower(regime.regime?.trend)].filter(Boolean).join(", "))
    : "";
  const adx = Number(regime?.adx);
  const H = pulse?.H ?? regime?.quantum?.H;
  const health = String(pulse?.overall || "").replace("_", " ");
  const hTone = H >= 0.5 ? "is-up" : H >= 0.3 ? "is-warn" : "is-down";
  const healthTip = pulse
    ? `System ${health || "unknown"} · NEXUS heartbeat ${agoSec(pulse.nexus_heartbeat_age_sec)} ago · Brain state ${agoSec(pulse.quantum_state_age_sec)} ago`
    : "Waiting for system pulse";
  const last = pulse?.last_alert;

  return (
    <div className="mk-row-a">
      <h1 className="mk-title">Markets</h1>

      <div className="mk-session" title="Today in your time zone: realized from closed trades plus open unrealized">
        <span className="mk-label">Session P&amp;L</span>
        {session.loading ? <span className="mk-skel-inline" /> : (
          <>
            <strong className={`mk-num ${tone(session.total)}`}>{money(session.total)}</strong>
            <span className="mk-session-split">
              <span>realized <b className={tone(session.realized)}>{money(session.realized)}</b></span>
              <span>open <b className={tone(session.open)}>{money(session.open)}</b></span>
            </span>
          </>
        )}
      </div>

      <div className="mk-chips">
        {regime ? (
          <span className={`mk-chip is-${macro.toLowerCase()}`}
            title={[regime.regime?.sentence, Number.isFinite(adx) && adx > 0 && regime.regime_label_source === "computed" ? `ADX ${adx.toFixed(1)}` : null].filter(Boolean).join(" · ")}>
            <b>{macro}</b>{regimeDetail ? ` · ${regimeDetail}` : ""}
            {Number.isFinite(adx) && adx > 0 && regime.regime_label_source === "computed" ? ` · ADX ${adx.toFixed(0)}` : ""}
          </span>
        ) : <span className="mk-chip is-loading">Regime…</span>}

        {regime ? (
          <span className={`mk-chip ${tilt.direction === "SHORT" ? "is-bear" : tilt.direction === "LONG" ? "is-bull" : ""}`}
            title="Direction of the bot's alerts over the last 24 hours">
            Bot tilt <b>{tilt.direction || "—"}</b> {tilt.long_pct ?? 0}L/{tilt.short_pct ?? 0}S · {tilt.total_alerts_24h ?? 0} alerts 24h
          </span>
        ) : null}

        <span className={`mk-chip ${hTone}`} title={healthTip} tabIndex={0}>
          Health <b className="mk-num">H {Number.isFinite(Number(H)) ? Number(H).toFixed(2) : "—"}</b>
          {health ? ` · ${health}` : ""}
        </span>

        {last?.symbol ? (
          <span className="mk-chip" title="Most recent bot alert">
            Last alert <b className={last.direction === "SHORT" ? "is-down" : "is-up"}>{base(last.symbol)} {last.direction}</b> · {agoSec(last.age_sec)} ago
          </span>
        ) : null}
      </div>

      <button type="button" className="mk-btn mk-refresh" onClick={onRefresh} disabled={refreshing}>
        <RefreshCw className={refreshing ? "mk-spin" : ""} aria-hidden /> Refresh
      </button>
    </div>
  );
}

function RowB({ metrics }) {
  return (
    <dl className="mk-row-b">
      {metrics.map((m) => (
        <div key={m.label} title={m.title || undefined}>
          <dt>{m.label}{m.scope ? <span className="mk-scope-inline"> · {m.scope}</span> : null}</dt>
          <dd className={`mk-num ${m.tone || ""}`}>{m.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function RowC({ view, onView, filter, onClearFilter }) {
  return (
    <nav className="mk-row-c" aria-label="Markets views">
      <div className="mk-tabs" role="tablist">
        {MARKET_VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={view === v.id}
            className={view === v.id ? "is-on" : ""} onClick={() => onView(v.id)}>
            {v.label}
          </button>
        ))}
      </div>
      {filter && filter !== "ALL" ? (
        <button type="button" className="mk-btn mk-filter-chip" onClick={onClearFilter} title="Clear the account filter">
          Showing {filter} only · Clear
        </button>
      ) : null}
    </nav>
  );
}

// The shell's .arena-root has overflow-x:hidden, so `position: sticky` never
// pins inside it. Rows A and C are re-rendered as a fixed bar once the real
// header scrolls away, aligned to the Markets column.
function useStuckBar(ref) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const place = () => {
      const r = el.getBoundingClientRect();
      if (r.bottom - 40 < 0) setBox((b) => (b && b.left === r.left && b.width === r.width ? b : { left: r.left, width: r.width }));
      else setBox(null);
    };
    const onScroll = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    place();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref]);
  return box;
}

export default function MarketsHeader({ view, onView, session, metrics, pulse, onRefresh, refreshing, filter, onClearFilter }) {
  const regimeFeed = useFeed(`${API}/dashboard/regime-banner`, { interval: 30000 });
  const ref = useRef(null);
  const stuck = useStuckBar(ref);
  useMarkets();

  const rowA = <RowA session={session} pulse={pulse} regime={regimeFeed.data} onRefresh={onRefresh} refreshing={refreshing} />;
  return (
    <>
      <header className="mk-header" ref={ref}>
        {rowA}
        <RowB metrics={metrics} />
        <RowC view={view} onView={onView} filter={filter} onClearFilter={onClearFilter} />
      </header>
      {stuck ? createPortal(
        <div className="mk mk-stuck" style={{ left: stuck.left, width: stuck.width }}>
          {rowA}
          <RowC view={view} onView={(v) => { onView(v); window.scrollTo({ top: 0 }); }} filter={filter} onClearFilter={onClearFilter} />
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export { pct };
